/**
 * Sessions View — Conversation history list.
 * Users can see past sessions, rename, delete, pin/unpin, tag, search, or start new ones.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { getServices, type SessionInfo, type SessionSearchResult } from "../services/index.ts";
import { PLATFORMS } from "../components/platform-icons.ts";

@customElement("katanoid-sessions-view")
export class KatanoidSessionsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private sessions: SessionInfo[] = [];
  @state() private loading = true;
  @state() private editingId: string | null = null;
  @state() private editingTitle = "";
  @state() private searchQuery = "";
  @state() private searchResults: SessionSearchResult[] = [];
  @state() private isSearching = false;
  @state() private filterTag: string | null = null;
  @state() private addingTagId: string | null = null;
  @state() private newTagValue = "";

  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadSessions();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  }

  private async loadSessions(): Promise<void> {
    this.loading = true;
    try {
      const { sessions } = getServices();
      this.sessions = await sessions.listSessions();
    } catch {
      this.sessions = [];
    }
    this.loading = false;
  }

  protected override render(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header">
          <h1 class="view-title">Sessions</h1>
          <button class="btn btn--primary btn--sm" @click=${this.handleNewSession}>
            + New Chat
          </button>
        </div>

        <!-- Search bar -->
        <div class="sessions-search">
          <input
            class="input sessions-search-input"
            type="text"
            placeholder="Search sessions..."
            .value=${this.searchQuery}
            @input=${this.handleSearchInput}
          />
          ${this.searchQuery
            ? html`<button class="sessions-search-clear" @click=${this.clearSearch}>&times;</button>`
            : nothing}
        </div>

        <!-- Tag filter -->
        ${this.renderTagFilter()}

        ${this.isSearching
          ? html`<div class="sessions-list"><div class="shimmer shimmer--card"></div></div>`
          : this.searchQuery && this.searchResults.length > 0
            ? this.renderSearchResults()
            : this.searchQuery && this.searchResults.length === 0
              ? html`<div class="sessions-empty"><p class="sessions-empty-desc">No results for "${this.searchQuery}"</p></div>`
              : this.loading
                ? html`
                    <div class="sessions-list">
                      <div class="shimmer shimmer--card"></div>
                      <div class="shimmer shimmer--card"></div>
                      <div class="shimmer shimmer--card"></div>
                    </div>
                  `
                : this.sessions.length === 0
                  ? this.renderEmptyState()
                  : this.renderSessionList()}
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    return html`
      <div class="sessions-empty">
        <div class="sessions-empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <h2 class="sessions-empty-title">No conversations yet</h2>
        <p class="sessions-empty-desc">
          Start a new chat and your conversation history will appear here.
        </p>
        <button class="btn btn--primary" @click=${this.handleNewSession}>
          Start a conversation
        </button>
      </div>
    `;
  }

  private renderTagFilter(): TemplateResult | typeof nothing {
    // Collect all unique tags across sessions
    const allTags = new Set<string>();
    for (const s of this.sessions) {
      if (s.tags) {
        for (const t of s.tags) allTags.add(t);
      }
    }
    if (allTags.size === 0) return nothing;

    return html`
      <div class="sessions-tag-filter">
        <button
          class="sessions-tag-pill ${this.filterTag === null ? "sessions-tag-pill--active" : ""}"
          @click=${() => { this.filterTag = null; }}
        >All</button>
        ${[...allTags].sort().map(
          (tag) => html`
            <button
              class="sessions-tag-pill ${this.filterTag === tag ? "sessions-tag-pill--active" : ""}"
              @click=${() => { this.filterTag = this.filterTag === tag ? null : tag; }}
            >${tag}</button>
          `
        )}
      </div>
    `;
  }

  private renderSearchResults(): TemplateResult {
    return html`
      <div class="sessions-list">
        <div class="sessions-group">
          <div class="sessions-group-label">Search Results</div>
          ${this.searchResults.map(
            (r) => html`
              <div class="session-card" @click=${() => this.handleOpenSession(r.sessionId)}>
                <div class="session-card-body">
                  <div class="session-card-title">${r.title}</div>
                  <div class="session-card-meta">
                    ${r.lastMessageAt
                      ? html`<span class="session-card-time">${this.formatTime(r.lastMessageAt)}</span>`
                      : nothing}
                    <span class="session-card-count">${r.messageCount} messages</span>
                    <span class="session-card-model">${this.formatModel(r.model)}</span>
                  </div>
                  ${r.matchContext
                    ? html`<div class="session-card-match">"...${r.matchContext}..."</div>`
                    : nothing}
                </div>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }

  private renderSessionList(): TemplateResult {
    // Filter by tag if active
    let filtered = this.sessions;
    if (this.filterTag) {
      filtered = this.sessions.filter(
        (s) => s.tags && s.tags.includes(this.filterTag!)
      );
    }

    // Separate pinned vs unpinned
    const pinned = filtered.filter((s) => s.pinned);
    const unpinned = filtered.filter((s) => !s.pinned);

    // Group unpinned sessions: today, yesterday, this week, older
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const weekAgo = new Date(today.getTime() - 86400000 * 7);

    const groups: { label: string; sessions: SessionInfo[] }[] = [];

    if (pinned.length > 0) {
      groups.push({ label: "Pinned", sessions: pinned });
    }

    const timeGroups: { label: string; sessions: SessionInfo[] }[] = [
      { label: "Today", sessions: [] },
      { label: "Yesterday", sessions: [] },
      { label: "This Week", sessions: [] },
      { label: "Older", sessions: [] },
    ];

    for (const s of unpinned) {
      const d = new Date(s.lastMessageAt ?? s.createdAt);
      if (d >= today) timeGroups[0].sessions.push(s);
      else if (d >= yesterday) timeGroups[1].sessions.push(s);
      else if (d >= weekAgo) timeGroups[2].sessions.push(s);
      else timeGroups[3].sessions.push(s);
    }

    groups.push(...timeGroups.filter((g) => g.sessions.length > 0));

    return html`
      <div class="sessions-list">
        ${groups.map(
          (g) => html`
            <div class="sessions-group">
              <div class="sessions-group-label">${g.label}</div>
              ${g.sessions.map((s) => this.renderSessionCard(s))}
            </div>
          `
        )}
      </div>
    `;
  }

  private renderSessionCard(s: SessionInfo): TemplateResult {
    const isEditing = this.editingId === s.id;
    const timeStr = this.formatTime(s.lastMessageAt ?? s.createdAt);
    const channelMeta = s.channel ? PLATFORMS[s.channel as keyof typeof PLATFORMS] : null;
    const isAddingTag = this.addingTagId === s.id;

    return html`
      <div class="session-card" @click=${() => this.handleOpenSession(s.id)}>
        <div class="session-card-body">
          ${isEditing
            ? html`
                <input
                  class="input session-card-edit-input"
                  .value=${this.editingTitle}
                  @input=${(e: InputEvent) => {
                    this.editingTitle = (e.target as HTMLInputElement).value;
                  }}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === "Enter") this.saveRename();
                    if (e.key === "Escape") { this.editingId = null; }
                  }}
                  @click=${(e: Event) => e.stopPropagation()}
                />
              `
            : html`<div class="session-card-title">${s.title}</div>`}
          <div class="session-card-meta">
            <span class="session-card-time">${timeStr}</span>
            <span class="session-card-count">${s.messageCount} messages</span>
            ${channelMeta
              ? html`
                  <span class="session-card-channel">
                    <span class="session-card-channel-icon" .innerHTML=${channelMeta.svg}></span>
                    ${channelMeta.name}
                  </span>
                `
              : nothing}
            <span class="session-card-model">${this.formatModel(s.model)}</span>
            ${s.forkedFromSessionId
              ? html`<span class="session-card-fork-badge">forked</span>`
              : nothing}
          </div>
          <!-- Tags row -->
          ${s.tags && s.tags.length > 0
            ? html`
                <div class="session-card-tags" @click=${(e: Event) => e.stopPropagation()}>
                  ${s.tags.map(
                    (tag) => html`
                      <span class="session-tag">
                        ${tag}
                        <button
                          class="session-tag-remove"
                          @click=${() => this.handleRemoveTag(s, tag)}
                          title="Remove tag"
                        >&times;</button>
                      </span>
                    `
                  )}
                </div>
              `
            : nothing}
          ${isAddingTag
            ? html`
                <div class="session-card-add-tag" @click=${(e: Event) => e.stopPropagation()}>
                  <input
                    class="input session-tag-input"
                    type="text"
                    placeholder="Tag name..."
                    .value=${this.newTagValue}
                    @input=${(e: InputEvent) => {
                      this.newTagValue = (e.target as HTMLInputElement).value;
                    }}
                    @keydown=${(e: KeyboardEvent) => {
                      if (e.key === "Enter") this.saveTag(s);
                      if (e.key === "Escape") { this.addingTagId = null; this.newTagValue = ""; }
                    }}
                  />
                </div>
              `
            : nothing}
        </div>
        <div class="session-card-actions" @click=${(e: Event) => e.stopPropagation()}>
          ${isEditing
            ? html`
                <button class="btn btn--ghost btn--sm" @click=${this.saveRename}>Save</button>
                <button class="btn btn--ghost btn--sm" @click=${() => { this.editingId = null; }}>Cancel</button>
              `
            : html`
                <button
                  class="btn btn--ghost btn--sm session-card-pin"
                  @click=${() => this.handleTogglePin(s)}
                  title="${s.pinned ? "Unpin" : "Pin"}"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="${s.pinned ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16h14v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 1 1 0 0 0 1-1V4H7v1a1 1 0 0 0 1 1 1 1 0 0 1 1 1z"/></svg>
                </button>
                <button class="btn btn--ghost btn--sm" @click=${() => this.startAddTag(s.id)} title="Add tag">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                </button>
                <button class="btn btn--ghost btn--sm" @click=${() => this.handleFork(s.id)} title="Fork">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9"/><path d="M12 12v3"/></svg>
                </button>
                <button class="btn btn--ghost btn--sm" @click=${() => this.startRename(s)} title="Rename">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button class="btn btn--ghost btn--sm session-card-delete" @click=${() => this.handleDelete(s.id)} title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
              `}
        </div>
      </div>
    `;
  }

  // ── Handlers ──

  private handleNewSession = async (): Promise<void> => {
    this.dispatchEvent(
      new CustomEvent("new-session", { bubbles: true, composed: true })
    );
  };

  private handleOpenSession(id: string): void {
    if (this.editingId) return;
    this.dispatchEvent(
      new CustomEvent("open-session", {
        detail: { sessionId: id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private startRename(s: SessionInfo): void {
    this.editingId = s.id;
    this.editingTitle = s.title;
  }

  private saveRename = async (): Promise<void> => {
    if (!this.editingId || !this.editingTitle.trim()) return;
    try {
      const { sessions } = getServices();
      await sessions.renameSession(this.editingId, this.editingTitle.trim());
      await this.loadSessions();
    } catch { /* ignore */ }
    this.editingId = null;
  };

  private handleFork(id: string): void {
    // Open the session in fork mode — the consumer-app will handle the rest
    this.dispatchEvent(
      new CustomEvent("open-session-fork", {
        detail: { sessionId: id },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleDelete = async (id: string): Promise<void> => {
    try {
      const { sessions } = getServices();
      await sessions.deleteSession(id);
      this.sessions = this.sessions.filter((s) => s.id !== id);
    } catch { /* ignore */ }
  };

  private handleSearchInput = (e: InputEvent): void => {
    this.searchQuery = (e.target as HTMLInputElement).value;

    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);

    if (!this.searchQuery.trim()) {
      this.searchResults = [];
      this.isSearching = false;
      return;
    }

    this.isSearching = true;
    this.searchDebounceTimer = setTimeout(() => this.executeSearch(), 300);
  };

  private clearSearch = (): void => {
    this.searchQuery = "";
    this.searchResults = [];
    this.isSearching = false;
  };

  private async executeSearch(): Promise<void> {
    const { sessions } = getServices();
    if (!sessions.searchSessions) {
      // Fallback: client-side title filter
      const q = this.searchQuery.toLowerCase();
      this.searchResults = this.sessions
        .filter((s) => s.title.toLowerCase().includes(q))
        .map((s) => ({
          sessionId: s.id,
          title: s.title,
          model: s.model,
          lastMessageAt: s.lastMessageAt,
          messageCount: s.messageCount,
          matchContext: s.title,
          matchSource: "title" as const,
        }));
      this.isSearching = false;
      return;
    }

    try {
      this.searchResults = await sessions.searchSessions(this.searchQuery);
    } catch {
      this.searchResults = [];
    }
    this.isSearching = false;
  }

  private handleTogglePin = async (s: SessionInfo): Promise<void> => {
    const { sessions } = getServices();
    if (!sessions.pinSession) return;

    const newPinned = !s.pinned;
    try {
      await sessions.pinSession(s.id, newPinned);
      // Update local state
      this.sessions = this.sessions.map((sess) =>
        sess.id === s.id ? { ...sess, pinned: newPinned } : sess
      );
    } catch {
      console.warn("[Sessions] Failed to toggle pin");
    }
  };

  private startAddTag(id: string): void {
    this.addingTagId = id;
    this.newTagValue = "";
  }

  private saveTag = async (s: SessionInfo): Promise<void> => {
    const tag = this.newTagValue.trim().toLowerCase();
    if (!tag) { this.addingTagId = null; return; }

    const { sessions } = getServices();
    if (!sessions.setTags) return;

    const newTags = [...(s.tags ?? []), tag];
    try {
      await sessions.setTags(s.id, newTags);
      this.sessions = this.sessions.map((sess) =>
        sess.id === s.id ? { ...sess, tags: newTags } : sess
      );
    } catch {
      console.warn("[Sessions] Failed to add tag");
    }
    this.addingTagId = null;
    this.newTagValue = "";
  };

  private handleRemoveTag = async (s: SessionInfo, tag: string): Promise<void> => {
    const { sessions } = getServices();
    if (!sessions.setTags) return;

    const newTags = (s.tags ?? []).filter((t) => t !== tag);
    try {
      await sessions.setTags(s.id, newTags);
      this.sessions = this.sessions.map((sess) =>
        sess.id === s.id ? { ...sess, tags: newTags } : sess
      );
    } catch {
      console.warn("[Sessions] Failed to remove tag");
    }
  };

  // ── Helpers ──

  private formatTime(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 86400000 * 7) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  private formatModel(model: string): string {
    if (model.includes("haiku")) return "Haiku";
    if (model.includes("sonnet")) return "Sonnet";
    if (model.includes("opus")) return "Opus";
    return model;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-sessions-view": KatanoidSessionsView;
  }
}
